import { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import type { ReactNode } from "react";
import { useOpenPlayOpportunities, useRestingOrders } from "../lib/queries.js";
import { useLiveEnabled } from "../lib/ws.js";
import { exposedPlays, playsFromOpportunities } from "../lib/plays.js";
import { AppIcon } from "./AppIcon.js";
import { NAV_POSITIONS_ATTR } from "../lib/flyToNav.js";
import { useIsAuthed } from "../lib/omauth.js";
import { useMarketMakingAccess } from "../lib/marketMaking.js";

/**
 * Left rail navigation. Collapses to an icon strip (persisted). Each item is a
 * route section of the desk; the active route is highlighted with an amber marker.
 */

const KEY = "gb.sidebar.collapsed";

export interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
}

/** Shared nav definition — used by the desktop rail and the mobile drawer. */
export const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Desk", end: true, icon: <IconDesk /> },
  { to: "/markets", label: "Markets", icon: <IconMarkets /> },
  // The Market screen (/market, docs/market-ux.md) isn't a section: the game page's market names
  // and lines open it.
  // Portfolio (was "Positions", before that "Orders"): working + open + recently settled.
  // /orders still works (redirects here).
  { to: "/portfolio", label: "Portfolio", icon: <IconPositions /> },
  // Quote both sides, watch fills/exposure, hedge (docs/market-making-ux.md).
  { to: "/market-making", label: "Market making", icon: <IconMarketMaking /> },
  // Signals, Arbitrage and Venue Activity are dropped from the nav for now (pages kept in src/pages; their
  // routes redirect to Markets — see main.tsx). Opportunities folded into Portfolio (plays, the
  // Exposed filter) and History (Bets / Plays); /opportunities redirects to Portfolio.
  { to: "/history", label: "History", icon: <IconCalendar /> },
];

/** Nav items for the current visitor. Signed out, `/` (the Desk) just redirects to Markets, so
 * it's dropped; every other item stays visible and leads to its Sign-in card. */
export function useNavItems(): NavItem[] {
  const authed = useIsAuthed();
  const marketMaking = useMarketMakingAccess();
  return NAV_ITEMS.filter((it) => (authed || it.to !== "/") && (marketMaking || it.to !== "/market-making"));
}

/** Live nav badge counts (resting orders, exposed plays) + WS liveness. */
export function useNavBadges(): { restingCount: number; exposedCount: number; live: boolean } {
  const restingQ = useRestingOrders();
  const restingCount = restingQ.data?.orders?.length ?? 0;
  const oppsQ = useOpenPlayOpportunities();
  const exposedCount = useMemo(() => exposedPlays(playsFromOpportunities(oppsQ.data ?? [])).length, [oppsQ.data]);
  const live = useLiveEnabled();
  return { restingCount, exposedCount, live };
}

/**
 * Portfolio's one badge: red with the exposed-play count when anything is exposed (urgent: you're
 * betting on one outcome), else the resting-order count (green when live, amber when polling).
 */
export function portfolioBadge(b: { restingCount: number; exposedCount: number; live: boolean }): {
  count: number;
  urgent: boolean;
  label: string;
} {
  const resting = `${b.restingCount} resting order${b.restingCount === 1 ? "" : "s"}`;
  if (b.exposedCount > 0) {
    const exposed = `${b.exposedCount} exposed play${b.exposedCount === 1 ? "" : "s"}`;
    return { count: b.exposedCount, urgent: true, label: b.restingCount > 0 ? `${exposed} · ${resting}` : exposed };
  }
  return { count: b.restingCount, urgent: false, label: resting };
}

export function Sidebar() {
  // Live badge counts — resting orders (green) + exposed opportunities (red), always current.
  const badges = useNavBadges();
  const { live } = badges;
  const navItems = useNavItems();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(KEY) === "1";
  });
  const toggle = () => {
    setCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <nav
      className={`hidden shrink-0 flex-col border-r border-border bg-panel-2 transition-[width] duration-150 lg:flex ${
        collapsed ? "w-[52px]" : "w-[180px]"
      }`}
    >
      <div className="flex h-11 items-center gap-2 border-b border-border px-3">
        <AppIcon size={24} />
        {!collapsed && (
          <span className="flex items-baseline gap-1.5 overflow-hidden">
            <span className="text-sm font-semibold tracking-tight text-ink">greenbook</span>
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-0.5 p-2">
        {navItems.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            title={collapsed ? it.label : undefined}
            // Landing pad for the "order handed off to Portfolio" parcel (lib/flyToNav.ts).
            {...(it.to === "/portfolio" ? { [NAV_POSITIONS_ATTR]: "" } : {})}
            className={({ isActive }) =>
              `group relative flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] ${
                isActive
                  ? "bg-raised text-ink"
                  : "text-dim hover:bg-raised/60 hover:text-ink"
              }`
            }
          >
            {({ isActive }) => {
              // Portfolio → exposed plays (red, urgent) when any, else resting orders (green).
              const pb = it.to === "/portfolio" ? portfolioBadge(badges) : null;
              const count = pb?.count ?? 0;
              const urgent = !!pb?.urgent;
              const badge = count > 0;
              const dot = urgent ? "bg-down" : live ? "bg-up" : "bg-amber";
              const pill = urgent ? "bg-down/15 text-down" : live ? "bg-up/15 text-up" : "bg-amber/15 text-amber";
              const label = pb?.label ?? "";
              return (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full bg-amber" />
                  )}
                  <span className="relative grid h-4 w-4 shrink-0 place-items-center">
                    {it.icon}
                    {badge && collapsed && (
                      <span
                        className={`absolute -right-1 -top-1 h-2 w-2 rounded-full ring-2 ring-panel-2 ${dot}`}
                        title={label}
                      />
                    )}
                  </span>
                  {!collapsed && <span className="break-words">{it.label}</span>}
                  {badge && !collapsed && (
                    <span
                      className={`ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${pill}`}
                      title={label}
                    >
                      {count}
                    </span>
                  )}
                </>
              );
            }}
          </NavLink>
        ))}
      </div>

      <button
        onClick={toggle}
        className="flex items-center gap-2 border-t border-border px-3 py-2 text-[11px] text-faint hover:text-dim"
        title={collapsed ? "expand" : "collapse"}
      >
        <span className="grid h-4 w-4 place-items-center">{collapsed ? "»" : "«"}</span>
        {!collapsed && <span>collapse</span>}
      </button>
    </nav>
  );
}

/* ------------------------------------------------------------------- icons */
// Simple 1.5px stroke line icons, currentColor.

function svg(children: ReactNode) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

function IconDesk() {
  return svg(
    <>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </>,
  );
}
function IconMarkets() {
  return svg(
    <>
      <path d="M3 3v18h18" />
      <path d="M7 15l3-4 3 2 4-6" />
    </>,
  );
}
function IconCalendar() {
  // month grid — History (P&L calendar)
  return svg(
    <>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 2v4M16 2v4" />
    </>,
  );
}
function IconMarketMaking() {
  // two opposing arrows — a bid on each side
  return svg(
    <>
      <path d="M4 8h14l-4-4" />
      <path d="M20 16H6l4 4" />
    </>,
  );
}
function IconPositions() {
  return svg(
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M9 9v11" />
    </>,
  );
}
